import Immerable from "./immerable";
import { DatasetImage } from "../datasets/dataset-image";
import { Dataset } from "../datasets/dataset";
import AnnotationQuery from "../components/annotation-page/models/AnnotationQuery";
import _ from "lodash";

/**
 * A work state models either the current "work" that is being worked on in RTViewer,
 * or the current annotation dataset index that is open on the annotation page.
 * 
 * (Currently the "work" effectively means annotation work, but in future could also
 * be something else.)
 * 
 * A work state object should be treated as immutable. If you need to modify a work
 * state object, deep clone it and modify the copy.
 * 
 * TODO: define the relationship between WorkState and ViewerState better.
 */
export default class WorkState extends Immerable {

    /** Current storage account of the dataset index, if any. */
    public readonly storageAccount: string | null;

    /** Current file share of the dataset index, if any. A file share is optional for dataset index. */
    public readonly fileShare: string | null;

    /** Dataset (annotation) image currently loaded in RTViewer.  */
    public readonly datasetImage: DatasetImage | null;

    /** Current active annotation dataset. RTViewer can as of now only work with one dataset at a time. */
    public readonly dataset: Dataset | null;

    /** If true the user allowed to modify current work. If false the user can only view the current
    * work in read-only mode. */
    public readonly canEdit: boolean;

    /** If true the user can create new RT structure sets within current work. */
    public readonly canCreateRtstruct: boolean;

    /** ID of a structure set that is currently active in RTViewer, if any. */
    public readonly structureSetId: string | null = null;

    /** Contains an error message if the current work is in an error state for any reason, null otherwise. */
    public readonly error: string | null = null;

    /** True if this work state models a dataset index, false otherwise. */
    public readonly isDatasetIndex: boolean;

    constructor(
        /** Current storage account of the dataset index, if any. */
        storageAccount: string | null, 
        /** Current file share of the dataset index, if any. A file share is optional for dataset index. */
        fileShare: string | null, 
        /** Current active annotation dataset, if any. */
        dataset: Dataset | null);
    constructor(
        /** Dataset (annotation) image currently loaded in RTViewer.  */
        datasetImage: DatasetImage | null,

        /** Current active annotation dataset. RTViewer can as of now only work with one dataset at a time. */
        dataset: Dataset | null,

        /** If true the user allowed to modify current work. If false the user can only view the current
         * work in read-only mode. */
        canEdit: boolean,

        /** If true the user can create new RT structure sets within current work. */
        canCreateRtstruct: boolean,

        /** ID of a structure set that is currently active in RTViewer, if any. */
        structureSetId?: string | null,

        /** Contains an error message if the current work is in an error state for any reason, null otherwise. */
        error?: string | null,
    );
    constructor(
        a: string | DatasetImage | null,
        b: string | Dataset | null,
        c: Dataset | null | boolean,
        canCreateRtstruct?: boolean,
        structureSetId?: string | null,
        error?: string | null,
    ) {
        super();

        this.datasetImage = a instanceof DatasetImage ? a : null;
        this.storageAccount = _.isString(a) ? a : null;
        this.dataset = b instanceof Dataset ? b : c instanceof Dataset ? c : null;
        this.fileShare = _.isString(b) ? b : null;
        this.canEdit = _.isBoolean(c) ? c : false;
        this.canCreateRtstruct = canCreateRtstruct !== undefined ? canCreateRtstruct : false;
        this.structureSetId = structureSetId !== undefined ? structureSetId : null;
        this.error = error !== undefined ? error : null;
        this.isDatasetIndex = canCreateRtstruct === undefined;
    }

    /** 
     * Returns true if current work has a valid dataset, a valid image, and is not in an error state. False otherwise.
     * Having storage account and/or fileshare defined does NOT count as having valid work.
     */
    hasWork(): boolean {
        return this.dataset !== null && this.datasetImage !== null && this.error === null;
    }

    /**
     * Returns true if current work state links to a dataset index, e.g. it has storage account and optionally
     * file share defined.
     */
    hasDatasetIndex(): boolean {
        return this.isDatasetIndex && this.storageAccount !== null;
    }

    /** Returns true if current work has a valid active structure set, false otherwise. */
    hasStructureSet(): boolean {
        return !!this.structureSetId;
    }

    /** Returns true if current work is in an error state, false otherwise. */
    hasError(): boolean {
        return this.error !== null;
    }

    /** Returns an error message if current work is in an error state, or an empty string if there is no current error.  */
    getErrorMessage(): string {
        return this.error || '';
    }

    /** Returns a short-hand name for the current work. */
    getWorkName(): string | undefined {
        if (this.dataset && this.datasetImage) {
            // return `${this.dataset.datasetFile.getShare().toString()}/${this.datasetImage.patientId}/${this.datasetImage.seriesDescription}`;
            return this.datasetImage.patientId;
        }

        return undefined;
    }

    /** Returns an annotation query matching current work. This will identify the current work uniquely to a certain degree -- enough to generate
     * a hyperlink, but not enough to replicate e.g. unsaved data, undo stacks, or used annotation filters. */
    getAnnotationQuery(): AnnotationQuery | undefined {
        if (this.hasWork()) {
            return new AnnotationQuery(
                this.dataset!.datasetFile.storageAccountName,
                this.dataset!.datasetFile.fileShareName,
                this.datasetImage!.patientId,
                this.datasetImage!.frameOfReferenceUid,
                this.datasetImage!.seriesId,
                this.structureSetId || undefined,
            )
        } else if (this.hasDatasetIndex()) {
            return new AnnotationQuery(this.storageAccount!, this.fileShare || undefined);
        }

        return undefined;
    }
}
